import React, { useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowRightOnRectangleIcon,
  Cog6ToothIcon,
  UserCircleIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';

import { useGlobalStore } from '@digiwedge/global-state';
import {
  colors,
  type AppLauncherHighlight,
  type AppLauncherItem,
  type BookingTransaction,
  type MenuItem,
  TeeTimeFooter,
  TeeTimeHeader,
  useFavourites,
  usePlayerBuddies,
  usePlayerCards,
  usePlayerTransactions,
  usePlayerWallet,
} from '@digiwedge/tee-time-ui';
import { findHelpLink, getHelpUrl } from '@digiwedge/ui-docs-link';
import { DOCS_CONFIG } from '../../utils/help-links.js';
import { teetimeAppLauncherItems } from '../navigation/teetimeAppLauncher.js';

const TOUR_STORAGE_KEY = 'teetime.tour.v1';
const TOUR_TRIGGER_KEY = `${TOUR_STORAGE_KEY}:trigger`;
const TOUR_ID = 'teetime-onboarding-v1';
const TOUR_COMPLETED_KEY = `${TOUR_STORAGE_KEY}:${TOUR_ID}:completed`;
const menuIconClassName = 'h-5 w-5';
const liveCountBadge = (count: number): string | undefined =>
  count > 0 ? String(count) : undefined;

const formatWalletBadge = (balance: number, currency = 'ZAR'): string =>
  new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(balance);

const buildBookingHighlight = (
  booking: BookingTransaction | undefined,
  formatDate: (iso: string) => string,
  formatTime: (iso: string) => string,
): AppLauncherHighlight | undefined => {
  if (!booking?.teeTime) {
    return undefined;
  }

  const venue = booking.clubName ?? booking.organisation?.displayName ?? 'Booking';
  const players = booking.players && booking.players > 0 ? ` · ${booking.players} players` : '';
  return {
    eyebrow: 'Next up',
    title: venue,
    description: `${formatDate(booking.teeTime)} · ${formatTime(booking.teeTime)}${players}`,
    href: '/bookings',
    ctaLabel: 'View booking',
  };
};

const enrichAppLauncherItems = (
  items: AppLauncherItem[],
  badges: Partial<Record<string, { badge: string; badgeTone?: AppLauncherItem['badgeTone'] }>>,
): AppLauncherItem[] =>
  items.map((item) => {
    const badge = badges[item.key];
    return badge ? { ...item, ...badge } : item;
  });

interface LayoutProps {
  children: React.ReactNode;
}

const Layout = ({ children }: LayoutProps): React.ReactElement => {
  const location = useLocation();
  const navigate = useNavigate();
  const isAuthenticated = useGlobalStore((state) => state.isAuthenticated);
  const profile = useGlobalStore((state) => state.profile);
  const playerEmail = isAuthenticated ? profile?.email : undefined;
  const walletSubjectKey = isAuthenticated ? (profile?.userId ?? profile?.email ?? null) : null;
  const {
    transactions,
    formatDate: formatBookingDate,
    formatTime: formatBookingTime,
    upcomingCount,
  } = usePlayerTransactions(playerEmail);
  const { wallet } = usePlayerWallet(isAuthenticated, {
    includeTransactions: false,
    subjectKey: walletSubjectKey,
  });
  const { cards } = usePlayerCards({ email: playerEmail });
  const { buddies } = usePlayerBuddies({ enabled: isAuthenticated });
  const { favouriteClubs } = useFavourites({ enabled: isAuthenticated });

  const bgImage =
    'https://images.unsplash.com/photo-1576220258822-153014832245?q=80&w=2086&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D';

  const handleLogoutNavigate = useCallback(() => {
    navigate('/logout');
  }, [navigate]);

  const handleTourRestart = useCallback(() => {
    try {
      // Clear completion flag and set trigger to restart the tour
      localStorage.removeItem(TOUR_COMPLETED_KEY);
      localStorage.setItem(TOUR_TRIGGER_KEY, '1');
      // Navigate to home to ensure tour elements are visible, then reload to trigger tour
      if (location.pathname !== '/') {
        navigate('/');
      }
      // Small delay to ensure navigation completes before reload
      setTimeout(() => window.location.reload(), 100);
    } catch (e) {
      console.warn('Failed to restart tour:', e);
    }
  }, [location.pathname, navigate]);

  const userMenuItems: MenuItem[] = useMemo(
    () =>
      isAuthenticated
        ? [
            {
              key: '/profile',
              label: 'Profile',
              description: 'Personal details and membership profile',
              icon: <UserCircleIcon className={menuIconClassName} />,
            },
            {
              key: '/profile?tab=buddies',
              label: 'Buddies',
              description: 'Linked players and regular groups',
              icon: <UserGroupIcon className={menuIconClassName} />,
            },
            {
              key: '/settings',
              label: 'Settings',
              description: 'Security, MFA, and linked accounts',
              icon: <Cog6ToothIcon className={menuIconClassName} />,
            },
            {
              key: '/logout',
              label: 'Logout',
              description: 'End this browser session',
              icon: <ArrowRightOnRectangleIcon className={menuIconClassName} />,
              onSelect: handleLogoutNavigate,
            },
          ]
        : [],
    [handleLogoutNavigate, isAuthenticated],
  );

  const resolvedProfileName = profile?.displayName ?? profile?.name ?? profile?.email ?? '';
  const resolvedAvatar = profile?.avatarUrl;
  const headerUser = isAuthenticated
    ? {
        name: resolvedProfileName || 'Profile',
        ...(profile?.email ? { email: profile.email } : {}),
        ...(resolvedAvatar ? { avatarUrl: resolvedAvatar } : {}),
      }
    : undefined;
  const linkedBuddyCount = useMemo(
    () => buddies.filter((buddy) => buddy.status !== 'pending').length,
    [buddies],
  );
  const nextBooking = useMemo(
    () => transactions.find((booking) => booking.isUpcoming && !booking.isCancelled),
    [transactions],
  );
  const appLauncherHighlight = useMemo(
    () =>
      isAuthenticated
        ? buildBookingHighlight(nextBooking, formatBookingDate, formatBookingTime)
        : undefined,
    [formatBookingDate, formatBookingTime, isAuthenticated, nextBooking],
  );
  const appLauncherItems = useMemo(() => {
    if (!isAuthenticated) {
      return teetimeAppLauncherItems;
    }

    const badges: Partial<
      Record<string, { badge: string; badgeTone?: AppLauncherItem['badgeTone'] }>
    > = {};
    const addCountBadge = (
      key: string,
      count: number,
      badgeTone?: AppLauncherItem['badgeTone'],
    ) => {
      const badge = liveCountBadge(count);
      if (badge) {
        badges[key] = badgeTone ? { badge, badgeTone } : { badge };
      }
    };

    addCountBadge('/bookings', upcomingCount, 'success');
    addCountBadge('/profile?tab=buddies', linkedBuddyCount);
    addCountBadge('/profile?tab=favourites', favouriteClubs.length);
    addCountBadge('/profile?tab=cards', cards.length);
    if (wallet) {
      badges['/wallet'] = {
        badge: formatWalletBadge(wallet.balance, wallet.currency),
        badgeTone: 'success',
      };
    }

    return enrichAppLauncherItems(teetimeAppLauncherItems, badges);
  }, [
    cards.length,
    favouriteClubs.length,
    isAuthenticated,
    linkedBuddyCount,
    upcomingCount,
    wallet,
  ]);

  // Help link for current route
  const helpEntry = useMemo(
    () => findHelpLink(location.pathname, DOCS_CONFIG.helpLinks),
    [location.pathname],
  );
  const helpUrl = useMemo(() => getHelpUrl(location.pathname, DOCS_CONFIG), [location.pathname]);

  return (
    <div
      style={{
        backgroundColor:
          location.pathname.includes('/login') || location.pathname.includes('/signup')
            ? undefined
            : '#f0f0f0',
        backgroundImage:
          location.pathname.includes('/login') || location.pathname.includes('/signup')
            ? `url(${bgImage})`
            : undefined,
        backgroundSize:
          location.pathname.includes('/login') || location.pathname.includes('/signup')
            ? 'cover'
            : undefined,
        backgroundPosition:
          location.pathname.includes('/login') || location.pathname.includes('/signup')
            ? 'left'
            : undefined,
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <TeeTimeHeader
        userMenuItems={userMenuItems}
        appLauncherItems={appLauncherItems}
        appLauncherHighlight={appLauncherHighlight}
        appLauncherLabel="TeeTime menu"
        user={headerUser}
        profileHref="/profile"
        profileLabel={resolvedProfileName || undefined}
        onTourRestart={handleTourRestart}
        helpUrl={helpUrl}
        helpLabel={helpEntry?.label}
      />
      <main
        style={{
          flex: '1 0 auto',
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
        }}
      >
        {children}
      </main>
      <TeeTimeFooter bgColor={colors.primary} borderColor={colors.eeZero} />
    </div>
  );
};

export default Layout;
